/**
 * Strategy Worker API Client
 * Handles all Strategy Worker v2 endpoints
 */

import { apiClient } from '../api-client';
import type { 
  StrategyStatus, 
  UserObjective, 
  QuerySpec, 
  StrategyMetrics,
  ResumeTag 
} from '../../types';

export class StrategyApi {
  /**
   * Get current strategy status for user
   */
  async getStatus(userId: string): Promise<StrategyStatus> {
    const response = await apiClient.get(`/strategy/status?user_id=${userId}`);
    return response.data;
  }

  /**
   * Get user's current objective and constraints
   */
  async getObjectives(userId: string): Promise<UserObjective> {
    const response = await apiClient.get(`/objectives/users/${userId}`);
    return response.data;
  }

  /**
   * Update user's objective and constraints
   */
  async updateObjectives(userId: string, objective: UserObjective): Promise<void> {
    await apiClient.put(`/objectives/users/${userId}`, objective);
  }

  /**
   * Get AI-powered objective recommendations
   */
  async getObjectiveRecommendations(userId: string): Promise<{
    recommended_objective: string;
    reasoning: string;
    confidence: number;
  }> {
    const response = await apiClient.post(`/objectives/recommend/${userId}`);
    return response.data;
  }

  /**
   * Reset user objective to default
   */
  async resetObjective(userId: string): Promise<void> {
    await apiClient.delete(`/objectives/users/${userId}`);
  }

  /**
   * Generate strategy recommendations (batch query generation)
   */
  async getRecommendations(userId: string, options?: {
    count?: number;
    exploration_rate?: number;
  }): Promise<{
    queries: QuerySpec[];
    attribution_summary: {
      strategies_used: string[];
      exploration_rate: number;
      total_queries: number;
    };
  }> {
    const response = await apiClient.post('/strategy/recommendations', {
      user_id: userId,
      ...options,
    });
    return response.data;
  }

  /**
   * Trigger strategy weight recomputation
   */
  async recomputeWeights(userId: string): Promise<{ job_id: string }> {
    const response = await apiClient.post('/learning/batch-update-weights', {
      user_id: userId,
    });
    return response.data;
  }

  /**
   * Get strategy performance metrics
   */
  async getMetrics(userId: string, timeframe?: '7d' | '30d' | '90d'): Promise<StrategyMetrics[]> {
    const params: Record<string, string | number | boolean> = timeframe ? { timeframe } : {};
    const response = await apiClient.get(`/learning/metrics/${userId}`, { params });
    return response.data;
  }

  /**
   * Record user interaction for learning
   */
  async recordReward(data: {
    user_id: string;
    job_id: string;
    action: 'save' | 'apply' | 'interview' | 'offer' | 'reject';
    strategy_key?: string;
    query_id?: string;
  }): Promise<void> {
    await apiClient.post('/learning/reward', data);
  }

  /**
   * Get resume tags for strategy lab
   */
  async getResumeTags(resumeId: string): Promise<ResumeTag[]> {
    const response = await apiClient.get(`/resumes/${resumeId}/tags`);
    return response.data;
  }

  /**
   * Build query specs for strategy lab (dry run)
   */
  async buildQueries(userId: string, options?: {
    dry_run?: boolean;
    count?: number;
  }): Promise<{
    queries: QuerySpec[];
    sources: {
      resume_tags: number;
      profile_data: boolean;
      objectives: boolean;
    };
  }> {
    const response = await apiClient.post('/strategy/build-queries', {
      user_id: userId,
      ...options,
    });
    return response.data;
  }

  /**
   * Generate tags from resume text
   */
  async generateTags(data: {
    resume_text: string;
    tag_types?: ('skill' | 'role' | 'domain' | 'experience')[];
  }): Promise<ResumeTag[]> {
    const response = await apiClient.post('/tags/from-resume', data);
    return response.data;
  }
}

export const strategyApi = new StrategyApi();